// src/hooks/useSubscriptions.js
// ─────────────────────────────────────────────────────────
// Encapsulates all loading / error / retry logic so pages
// stay clean and only deal with { services, loading, error }
// ─────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchSubscriptions } from "../services/subscriptionApi";

/**
 * useSubscriptions(phone, network)
 *
 * Returns:
 *   services  — array of normalised service objects ([] while loading)
 *   loading   — true while fetching
 *   error     — error message string or null
 *   retry     — call this to re-fetch manually
 */
const useSubscriptions = (phone, network) => {
  const [services, setServices]   = useState([]);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState(null);

  // Tracks the AbortController of the *current* in-flight request so that
  // a stale/duplicate call (e.g. React.StrictMode's dev double-invoke, or
  // phone/network changing mid-flight) can never overwrite fresh state.
  const controllerRef = useRef(null);

  const load = useCallback(async () => {
    if (!phone || !network) return;

    // Cancel any previous in-flight request before starting a new one
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    setError(null);
    setServices([]);

    try {
      const data = await fetchSubscriptions(phone, network);

      if (controller.signal.aborted) return; // stale response, ignore
      setServices(data);
    } catch (err) {
      if (controller.signal.aborted) return; // stale error, ignore
      console.error("useSubscriptions:", err);
      setError(err.message || "Kuch masla aa gaya. Dobara koshish karein.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [phone, network]);

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  return { services, loading, error, retry: load };
};

export default useSubscriptions;